import { z } from 'zod';

import {
  PAYOUT_KINDS,
  PAYOUT_PROGRESS_STATUSES,
  PAYOUT_STATUSES,
  TAX_MODES,
} from '../enums';
import {
  zCurrency,
  zISODate,
  zId,
  zMoney,
  zMoneyNonNegative,
  zNonEmptyString,
  zOptionalString,
  zPagination,
  zSort,
  zTaxRate,
} from './common';

/* ─── Создание начисления ───────────────────────────────────────────────── */

/** Строка-корректировка: бонус или штраф внутри одного начисления (F-34). */
export const adjustmentSchema = z.object({
  type: z.enum(['BONUS', 'PENALTY']),
  amountMinor: zMoney,
  note: zOptionalString(200),
});
export type AdjustmentInput = z.infer<typeof adjustmentSchema>;

export const createPayoutSchema = z.object({
  personId: zId,
  /** Быстрый сценарий: сумма, дата, кошелёк. Остальное подставляется. */
  baseMinor: zMoney,
  dueDate: zISODate,
  walletId: zId.nullish(),

  /** Расширенная часть — скрыта за «Подробнее». */
  kind: z.enum(PAYOUT_KINDS).default('SALARY'),
  contractId: zId.nullish(),
  periodKey: z
    .string()
    .regex(/^\d{4}-\d{2}$/, { message: 'validation.invalidPeriod' })
    .nullish(),
  currency: zCurrency.optional(),
  taxMode: z.enum(TAX_MODES).optional(),
  taxRate: zTaxRate.optional(),
  note: zOptionalString(500),
  customFields: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),

  /**
   * Бонусы и штрафы одной формой (F-34). Каждая строка сохраняется отдельным
   * начислением с ссылкой на родителя — история остаётся разбираемой,
   * а не превращается в одну непрозрачную сумму.
   */
  adjustments: z.array(adjustmentSchema).max(20).default([]),

  progressStatus: z.enum(PAYOUT_PROGRESS_STATUSES).default('NOT_STARTED'),

  /** Создать сразу оплаченным — «Начислить и выплатить». */
  payImmediately: z.boolean().default(false),
});
export type CreatePayoutInput = z.infer<typeof createPayoutSchema>;

/**
 * Премия или штраф, заведённые в момент события.
 *
 * Раньше корректировку можно было внести только внутри формы начисления —
 * то есть в конце месяца, когда начисление появится. До тех пор премии и
 * штрафы приходилось держать где-то на стороне: в заметках, в переписке,
 * в чужой голове. Что-нибудь из этого обязательно терялось.
 *
 * Теперь запись заводится сразу и сама прикрепляется к начислению за свой
 * период, когда оно появится. Если начисление за период уже есть и ещё не
 * выплачено — прикрепляется немедленно.
 */
export const createAdjustmentSchema = z
  .object({
    personId: zId,
    type: z.enum(['BONUS', 'PENALTY']),
    /**
     * Как задана величина.
     *
     * Половину премий назначают в процентах от оклада («месячная премия 20%»),
     * и переводить их в рубли в уме — лишний шаг, на котором ошибаются.
     * Сумма при этом всё равно считается и фиксируется сразу: оклад потом
     * поменяется, а уже начисленное меняться задним числом не должно.
     */
    mode: z.enum(['FIXED', 'PERCENT']).default('FIXED'),
    amountMinor: zMoney.optional(),
    percent: z.number().min(0.01).max(100).optional(),
    /** Месяц, к которому относится. По умолчанию — текущий. */
    periodKey: z.string().regex(/^\d{4}-\d{2}$/, { message: 'validation.invalidPeriod' }),
    /**
     * За что. Не обязательное поле, но через месяц «штраф 5 000» без причины
     * не объяснит уже никто.
     */
    note: zOptionalString(300),
  })
  .refine((value) => (value.mode === 'PERCENT' ? value.percent : value.amountMinor), {
    message: 'validation.amountRequired',
    path: ['amountMinor'],
  });
export type CreateAdjustmentInput = z.infer<typeof createAdjustmentSchema>;

/** Разбивка одного начисления на аванс и остаток. */
export const createSplitPayoutSchema = z.object({
  personId: zId,
  contractId: zId.nullish(),
  periodKey: z.string().regex(/^\d{4}-\d{2}$/),
  advanceMinor: zMoneyNonNegative,
  advanceDueDate: zISODate,
  remainderMinor: zMoneyNonNegative,
  remainderDueDate: zISODate,
  walletId: zId.nullish(),
  taxMode: z.enum(TAX_MODES).optional(),
  taxRate: zTaxRate.optional(),
  note: zOptionalString(500),
});
export type CreateSplitPayoutInput = z.infer<typeof createSplitPayoutSchema>;

export const updatePayoutSchema = z.object({
  baseMinor: zMoneyNonNegative.optional(),
  dueDate: zISODate.optional(),
  walletId: zId.nullish(),
  kind: z.enum(PAYOUT_KINDS).optional(),
  taxMode: z.enum(TAX_MODES).optional(),
  taxRate: zTaxRate.optional(),
  note: zOptionalString(500),
  status: z.enum(['DRAFT', 'SCHEDULED', 'CANCELED']).optional(),
  progressStatus: z.enum(PAYOUT_PROGRESS_STATUSES).optional(),
});
export type UpdatePayoutInput = z.infer<typeof updatePayoutSchema>;

/* ─── Выплата ───────────────────────────────────────────────────────────── */

export const payPayoutSchema = z.object({
  amountMinor: zMoney,
  walletId: zId,
  paidAt: zISODate,
  /**
   * Комиссия сети или банка.
   *
   * Отправили игроку 500 USDT — с кошелька ушло 501. Человеку закрывается
   * 500, с кошелька списывается 501, комиссия уходит в расходы отдельной
   * строкой. Без этого остаток кошелька уезжает от реального на каждом
   * переводе, и через месяц цифрам перестают верить.
   */
  feeMinor: zMoneyNonNegative.default(0),
  note: zOptionalString(300),
});
export type PayPayoutInput = z.infer<typeof payPayoutSchema>;

/**
 * Исход по одному начислению в массовой выплате.
 *
 * Раньше ответ состоял из двух чисел, «выплачено» и «пропущено», и в
 * «пропущено» попадало всё сразу: и уже оплаченное, и отказ по правилу, и
 * технический сбой. Менеджер видел «выплачено 9, пропущено 3», понимал это
 * как «трое уже были оплачены» и закрывал окно. Трое оставались без денег,
 * и в системе об этом не было ни следа.
 */
export type BulkPayOutcome =
  /** Оплачено этим запросом. */
  | 'paid'
  /** Платить было нечего: начисление уже закрыто. */
  | 'alreadyPaid'
  /** Отказ по бизнес-правилу — например, кошелёк в другой валюте. */
  | 'rejected'
  /** Технический сбой: запись не удалась. Деньги не ушли. */
  | 'failed';

export interface BulkPayItemResult {
  payoutId: string;
  personNickname: string | null;
  outcome: BulkPayOutcome;
  amountMinor: number;
  /** Причина отказа для показа человеку. Пусто для успешных. */
  reason: string | null;
}

export interface BulkPayResult {
  paid: number;
  /** Уже оплаченные — нормальный ход, отдельно от неудач. */
  alreadyPaid: number;
  /** Отклонённые по правилу. */
  rejected: number;
  /** Не записались по технической причине. Это и есть повод бить тревогу. */
  failed: number;
  totalMinor: number;
  items: BulkPayItemResult[];
}

export const bulkPaySchema = z.object({
  payoutIds: z.array(zId).min(1).max(100),
  walletId: zId,
  paidAt: zISODate,
  /** Комиссия за перевод — одна на каждую выплату в пачке. */
  feePerPayoutMinor: zMoneyNonNegative.default(0),
});

/* ─── Массовая генерация зарплаты ───────────────────────────────────────── */

export const generateSalarySchema = z.object({
  periodKey: z.string().regex(/^\d{4}-\d{2}$/, { message: 'validation.invalidPeriod' }),
  /** Кого включаем; пусто — всех с активными контрактами. */
  personIds: z.array(zId).optional(),
  /** Разбивать ли на аванс и остаток по правилам контракта. */
  splitAdvance: z.boolean().default(true),
  walletId: zId.nullish(),
});
export type GenerateSalaryInput = z.infer<typeof generateSalarySchema>;

/* ─── Штрафы и вычеты (F-02) ────────────────────────────────────────────── */

export const createDeductionSchema = z.object({
  personId: zId,
  amountMinor: zMoney,
  dueDate: zISODate,
  reason: zNonEmptyString(300),
  /** Списать из ближайшего неоплаченного начисления, а не создавать отдельное. */
  applyToNextPayout: z.boolean().default(true),
});
export type CreateDeductionInput = z.infer<typeof createDeductionSchema>;

/* ─── Призовые (F-03) ───────────────────────────────────────────────────── */

export const createPrizeSchema = z.object({
  tournamentName: zNonEmptyString(120),
  place: z.number().int().min(1).max(999).nullish(),
  totalMinor: zMoney,
  currency: zCurrency,
  receivedAt: zISODate,
  walletId: zId.nullish(),
  /** Доля организации в процентах; остальное делится между людьми. */
  orgSharePercent: z.number().min(0).max(100).default(0),
  /**
   * Равное деление или по коэффициентам. При равном весами не пользуемся:
   * иначе «поровну» зависело бы от того, что осталось в форме от прошлого раза.
   */
  splitMode: z.enum(['EQUAL', 'WEIGHTED']).default('EQUAL'),
  shares: z
    .array(
      z.object({
        personId: zId,
        weight: z.number().min(0).max(1000).default(1),
      }),
    )
    .min(1),
  /** Создать начисления людям сразу. */
  createPayouts: z.boolean().default(true),
  payoutDueDate: zISODate.optional(),
  note: zOptionalString(500),
})
  .refine(
    (value) =>
      value.splitMode !== 'WEIGHTED' ||
      value.shares.reduce((sum, share) => sum + share.weight, 0) > 0,
    {
      // При нулевой сумме весов деление даёт всем по нулю: приз зачислялся бы
      // на кошелёк целиком, начисления людям не создавались, а форма
      // показывала бы, что состав что-то получил.
      message: 'validation.allWeightsZero',
      path: ['shares'],
    },
  );
export type CreatePrizeInput = z.infer<typeof createPrizeSchema>;

/* ─── Фильтры ───────────────────────────────────────────────────────────── */

export const payoutFilterSchema = zPagination.merge(zSort).extend({
  personId: zId.optional(),
  status: z.enum(PAYOUT_STATUSES).optional(),
  kind: z.enum(PAYOUT_KINDS).optional(),
  periodKey: z.string().regex(/^\d{4}-\d{2}$/).optional(),
  from: zISODate.optional(),
  to: zISODate.optional(),
  progressStatus: z.enum(PAYOUT_PROGRESS_STATUSES).optional(),
  /** Готовые наборы для вкладок экрана «Выплаты». */
  preset: z.enum(['upcoming', 'overdue', 'drafts', 'paid', 'all']).optional(),
});
export type PayoutFilter = z.infer<typeof payoutFilterSchema>;

/* ─── Ответы ────────────────────────────────────────────────────────────── */

export interface PayoutAdjustmentDto {
  id: string;
  type: 'BONUS' | 'PENALTY';
  amountMinor: number;
  note: string | null;
  status: string;
  paidMinor: number;
}

/**
 * Премия или штраф, ещё не вошедшие ни в одно начисление.
 *
 * Показываются отдельным списком: незамеченная запись — это ровно та потеря,
 * ради предотвращения которой всё и заводилось.
 */
export interface PendingAdjustmentDto {
  id: string;
  personId: string;
  personNickname: string;
  type: 'BONUS' | 'PENALTY';
  amountMinor: number;
  /** Процент, которым задали, — если задавали процентом. */
  percentOfBase: number | null;
  currency: string;
  periodKey: string | null;
  note: string | null;
  createdAt: string;
}

export interface PendingAdjustmentsResponse {
  items: PendingAdjustmentDto[];
  /** Итог по валютам: премии минус штрафы. */
  totals: { currency: string; amountMinor: number }[];
}

export interface PayoutPaymentDto {
  id: string;
  amountMinor: number;
  /** Комиссия перевода: с кошелька ушло amountMinor + feeMinor. */
  feeMinor: number;
  paidAt: string;
  transactionId: string;
  walletName: string | null;
}

export interface PayoutDto {
  id: string;
  personId: string;
  personNickname: string;
  personKind: string;
  contractId: string | null;

  kind: string;
  periodKey: string | null;
  isDeduction: boolean;

  baseMinor: number;
  taxMode: string;
  taxRate: number;
  taxMinor: number;
  netMinor: number;
  orgCostMinor: number;
  currency: string;

  dueDate: string;
  status: string;
  /** Ручной статус обработки, независимый от денежного (F-35). */
  progressStatus: string;
  paidMinor: number;
  remainderMinor: number;
  daysUntilDue: number;

  walletId: string | null;
  walletName: string | null;
  paidAt: string | null;
  note: string | null;

  payments: PayoutPaymentDto[];
  parentPayoutId: string | null;
  /** Бонусы и штрафы, привязанные к этому начислению. */
  adjustments: PayoutAdjustmentDto[];
  /** Итог с учётом бонусов и штрафов — то, что реально получит человек. */
  totalNetMinor: number;
  createdAt: string;
}

export interface PayoutListResponse {
  items: PayoutDto[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  summary: {
    /** Всего к выплате по текущему фильтру. */
    dueMinor: number;
    /** Из них просрочено. */
    overdueMinor: number;
    paidMinor: number;
    count: number;
    overdueCount: number;
  };
}

/** Предпросмотр массовой генерации: что именно будет создано. */
export interface SalaryPreviewRow {
  personId: string;
  personNickname: string;
  contractId: string;
  currency: string;
  salaryMinor: number;
  advanceMinor: number;
  advanceDueDate: string | null;
  remainderMinor: number;
  remainderDueDate: string;
  taxMode: string;
  taxRate: number;
  taxMinor: number;
  orgCostMinor: number;
  /** Начисление за этот период уже существует. */
  alreadyExists: boolean;
}

export interface SalaryPreviewResponse {
  periodKey: string;
  rows: SalaryPreviewRow[];
  totals: {
    salaryMinor: number;
    taxMinor: number;
    orgCostMinor: number;
    peopleCount: number;
    skippedCount: number;
  };
  /** Валюты контрактов, если их несколько — считаем каждую отдельно. */
  currencies: string[];
}

export interface PrizeShareDto {
  id: string;
  personId: string;
  personNickname: string;
  weight: number;
  amountMinor: number;
  payoutId: string | null;
  /** Начисление по доле уже закрыто. */
  isPaid: boolean;
}

export interface PrizeEventDto {
  id: string;
  tournamentName: string;
  place: number | null;
  totalMinor: number;
  currency: string;
  receivedAt: string;
  orgSharePercent: number;
  orgAmountMinor: number;
  distributedMinor: number;
  walletId: string | null;
  walletName: string | null;
  note: string | null;
  shares: PrizeShareDto[];
  createdAt: string;
}
